import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, CheckCircle2, Boxes } from "lucide-react";
import { toast } from "sonner";

const BUSINESS_MODELS = ["On Demand", "Crowd Funding"];
const ORDER_VOLUMES = ["0-299", "300-499", "500-999", "1000-1999", "2000-4999", "5000-9999", "10000-19999", "20000+"];
const HELP_MAX = 250;

export default function ThreePLFlow({ onBack }) {
  const [form, setForm] = useState({
    full_name: "", work_email: "", website_url: "", phone: "", company_name: "", company_location: "",
    business_model: "", order_volume: "", manufacturing_location: "", annual_revenue: "", help_text: "",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const required = {
      "Full Name": form.full_name, "Work Email": form.work_email, "Website URL": form.website_url,
      "Phone Number": form.phone, "Company Name": form.company_name, "Company Location": form.company_location,
      "Business Model": form.business_model, "Current Order Volume": form.order_volume, "Manufacturing Location": form.manufacturing_location,
    };
    const missing = Object.entries(required).filter(([, v]) => !String(v).trim()).map(([k]) => k);
    if (missing.length) return toast.error(`Please fill: ${missing.join(", ")}`);
    if (!consent) return toast.error("Please accept the marketing consent to continue");
    setLoading(true);
    try {
      await api.post("/3pl/enquiry", { ...form, marketing_consent: consent });
      setDone(true);
      toast.success("Enquiry submitted");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-lg border border-border bg-card p-10 text-center">
          <div className="h-14 w-14 bg-emerald-500/15 text-emerald-500 grid place-items-center mx-auto mb-6"><CheckCircle2 className="h-8 w-8" /></div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Thank you!</h1>
          <p className="text-muted-foreground mt-2">Your enquiry has been received. An e-Commerce fulfilment expert will get in touch within the next business day.</p>
          <Button className="mt-6 rounded-none" onClick={onBack} data-testid="threepl-done">Back to services</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <button type="button" onClick={onBack} data-testid="threepl-back" className="mb-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors"><ArrowLeft className="h-4 w-4" /> Back to services</button>
      <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2 flex items-center gap-2"><Boxes className="h-4 w-4" /> 3PL Services</div>
      <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-[0.95]">Talk to our Fulfillment Experts</h1>
      <p className="text-muted-foreground mt-3">Complete the form below, and an e-Commerce fulfilment expert will get in touch within the next business day.</p>

      <section className="border border-border bg-card p-6 mt-8 space-y-4" data-testid="threepl-form">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full Name" req v={form.full_name} on={(v) => set("full_name", v)} id="full_name" />
          <Field label="Work Email" req type="email" v={form.work_email} on={(v) => set("work_email", v)} id="work_email" />
          <Field label="Website URL" req v={form.website_url} on={(v) => set("website_url", v)} id="website_url" placeholder="https://" />
          <Field label="Phone Number" req v={form.phone} on={(v) => set("phone", v)} id="phone" />
          <Field label="Company Name" req v={form.company_name} on={(v) => set("company_name", v)} id="company_name" />
          <Field label="Company Location" req v={form.company_location} on={(v) => set("company_location", v)} id="company_location" />
          <SelectField label="Business Model" req v={form.business_model} on={(v) => set("business_model", v)} id="business_model" options={BUSINESS_MODELS} placeholder="Select a model" />
          <SelectField label="Current Order Volume (Monthly)" req v={form.order_volume} on={(v) => set("order_volume", v)} id="order_volume" options={ORDER_VOLUMES} placeholder="Select a range" />
          <Field label="Manufacturing Location" req v={form.manufacturing_location} on={(v) => set("manufacturing_location", v)} id="manufacturing_location" />
          <Field label="Annual Recurring Revenue" v={form.annual_revenue} on={(v) => set("annual_revenue", v)} id="annual_revenue" placeholder="Optional" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="help_text">What do you need help with</Label>
          <Textarea id="help_text" value={form.help_text} maxLength={HELP_MAX}
            onChange={(e) => set("help_text", e.target.value.slice(0, HELP_MAX))}
            className="rounded-none" rows={4} data-testid="field-help_text" placeholder="Optional" />
          <div className="text-xs text-muted-foreground text-right" data-testid="help-counter">{form.help_text.length} of {HELP_MAX} max characters</div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="threepl-consent-label">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 rounded-none" data-testid="threepl-consent" />
          <span className="text-sm text-muted-foreground leading-snug">I agree to receive promotional communications from Arrow and its partners. I understand that I can unsubscribe at any time. Please refer to our Privacy Policy for more information.</span>
        </label>

        <div className="flex justify-end pt-2">
          <Button type="button" disabled={loading} onClick={submit} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="threepl-submit">
            {loading ? "Submitting…" : <>Submit <ArrowRight className="h-4 w-4" /></>}
          </Button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, req, v, on, id, type = "text", placeholder }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}{req && <span className="text-primary"> *</span>}</Label>
      <Input id={id} type={type} value={v} onChange={(e) => on(e.target.value)} className="rounded-none" placeholder={placeholder} data-testid={`field-${id}`} />
    </div>
  );
}

function SelectField({ label, req, v, on, id, options, placeholder }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}{req && <span className="text-primary"> *</span>}</Label>
      <select id={id} value={v} onChange={(e) => on(e.target.value)} data-testid={`field-${id}`}
        className="w-full h-10 rounded-none border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
